import { Store } from '@ngrx/store';
import { ConfigurationService } from '../../entry-routes/configuration/configuration.service';
import { SocketService } from './socket/socket.service';
import { RouterPathService } from '../redux/router-path/service/router-path.service';
import { Router } from '@angular/router';
import { I18nService } from './i18n/i18n.service';
import { ModuleFromRouteDefinerService } from './module-from-route-definer/module-from-route-definer.service';
import { MenuService } from '../redux/menu/service/menu.service';
import { DomainService } from '../redux/domain/service/domain.service';
import { SearchResultPaginationAction } from '../redux/search-result-pagination/action/search-result-pagination.action';
import { Injectable, Injector } from '@angular/core';
import { catchError, filter, switchMap, take, tap } from 'rxjs/operators';
import { combineLatest, EMPTY, Subscription } from 'rxjs';
import { selectAuthenticationAuthenticated } from '../../entry-routes/authentication/authentication.selectors';
import { selectDomainsDatas } from '../redux/domain/selectors/domain.selectors';
import { selectMaintenanceInfoType } from '../redux/maintenance-info/maintenance-info-selectors';
import { MaintenanceType } from '../models/maintenance-info';
import { createCustomElement, NgElementConfig } from '@angular/elements';
import {
    ManualSignatureComponent
} from '../ui/details/components/signature/manual-signature/manual-signature.component';
import {ContactComponent} from '../ui/details/components/task-list/contact/contact.component';
import { DynamicScriptLoaderService } from './dynamic-script-loader/dynamic-script-loader.service';

@Injectable({ providedIn: 'root' })
export class AppInitializerService {

    subs: Subscription[] = [];
    private authenticated$ = this.store.select(selectAuthenticationAuthenticated);
    private authenticatedIsTrue$ = this.authenticated$.pipe(filter(isAuthenticated => isAuthenticated), take(1))

    constructor(    private store: Store,
                    private configurationService: ConfigurationService,
                    private socketService: SocketService,
                    private routerPathService: RouterPathService,
                    private router: Router,
                    private configService: ConfigurationService,
                    private i18nService: I18nService,
                    private moduleFromRouteDefinerService: ModuleFromRouteDefinerService,
                    private menuService: MenuService,
                    private domainService: DomainService,
                    private searchResultPaginationAction: SearchResultPaginationAction,
                    private injector: Injector,
                    private dynamicScriptLoaderService : DynamicScriptLoaderService) {}

    init() {
        // Code d'initialisation
        this.configurationService.loadConfiguration()
            .pipe(
                tap(() => this.routerPathService.onInit()),
                switchMap(() => this.authenticatedIsTrue$),
                filter(authenticated => authenticated),
                catchError((err: any) => {
                    this.router.navigate(['error', {error: err}]).then();
                    return EMPTY;
                })
            )
            .subscribe(() => {
                this.socketService.init();
                this.dynamicScriptLoaderService.loadEternalScriptOfHOTJAR();
            });

        this.subs.push(this.authenticatedIsTrue$.pipe(tap(() => {
            console.log(`AUTHENTIFICATION OK`)
        }), switchMap(() => this.i18nService.loadI18n())).subscribe());
        this.subs.push(this.authenticatedIsTrue$.pipe(switchMap(() => this.i18nService.reloadI18nOnLangChange())).subscribe());
        this.subs.push(this.moduleFromRouteDefinerService.watchAndDefineModuleFromRoute().subscribe());
        this.subs.push(this.authenticatedIsTrue$.pipe(switchMap(() => this.menuService.loadMenuItems())).subscribe());
        this.loadWorkspaceItemsIfNotAlreadyLoaded()
        this.subs.push(this.configService.whenReady(() => this.reset_after_maintenance()).subscribe());
        this.searchResultPaginationAction.loadSearchResultPagination();

        // Définition du Web Component
        const ngElementConfig : NgElementConfig = { injector : this.injector};
        const manualSignature = createCustomElement(ManualSignatureComponent, ngElementConfig );
        const contactChange = createCustomElement(ContactComponent, ngElementConfig );

        // Enregistrement du Web Component
        customElements.define('manual-signature', manualSignature);
        customElements.define('contact-change', contactChange);

    }

    private loadWorkspaceItemsIfNotAlreadyLoaded() {
        const noDomainsAreLoaded$ = this.store.select(selectDomainsDatas).pipe(
            filter(datas => !datas || datas.length == 0), take(1),
        )
        this.subs.push(this.authenticatedIsTrue$.pipe(
            switchMap(() => noDomainsAreLoaded$),
            switchMap(() => this.domainService.loadWorkspaceItems())
        ).subscribe());

    }

    private reset_after_maintenance() {
        return this.store.select(selectMaintenanceInfoType).pipe(
            filter(type => type === MaintenanceType.MAINTENANCE_OFF),
            tap(() => () => this.store.dispatch({type: 'RESET_AFTER_MAINTENANCE'})),
            switchMap(() => combineLatest([
                this.i18nService.loadI18n().pipe(take(1)),
                this.menuService.loadMenuItems(),
                this.domainService.loadWorkspaceItems()
            ])),
            tap(() => this.searchResultPaginationAction.loadSearchResultPagination())
        );
    }
}
